/* eslint-disable max-len */
import { single } from 'validate.js';
import get from 'lodash/get';

import { checkUserRightHelper } from '@crm-framework/utils';
import { getUser } from '@crm-framework/auth';
import { requiredValidation } from '@crm-framework/validation';

import { getIsCryptoCrm } from '@crm-package/app';
import { getDictionaryByNameSelector } from '@crm-package/dictionaries';

import {
  PROMO_TYPE,
  limitTypesKeys,
  limitTypes,
  TOTAL_DEPOSIT_FROM,
  TOTAL_DEPOSIT_TO,
  TOTAL_BONUS_AMOUNT,
} from '../consts';
import {
  IPromoModel,
  IBonusConditions,
  IText,
  IBonusConditionsErrors,
  IPromoErrors,
  ICheckPromoBusinessUnitsByPromoEditRight,
} from '../types';
import { checkPromoBusinessUnitsByPromoEditRight } from '../utils';

import { constraints } from './constraints';

export const getEditBUInfoMessage = ({
  promoBusinessUnits,
  rights,
  businessUnitRights,
  businessUnits,
}: ICheckPromoBusinessUnitsByPromoEditRight) => {
  const hasPromoEditRight = checkUserRightHelper(rights, 'Promo Edit');

  if (!hasPromoEditRight) return 'You do not have the right to edit a promo.';

  if (!promoBusinessUnits.length) return 'Field is required';

  const hasPromoEditRightByPromoBUs = checkPromoBusinessUnitsByPromoEditRight({
    promoBusinessUnits,
    rights,
    businessUnitRights,
    businessUnits,
  });

  if (!hasPromoEditRightByPromoBUs)
    return 'You do not have the right to edit a promo from another business unit.';

  return null;
};

export function validate(data: IPromoModel, state: any) {
  const isCryptoCrm = getIsCryptoCrm(state);
  let precision = 0;

  if (isCryptoCrm) {
    precision = 14;
  } else {
    precision = 2;
  }

  const minNumber = `0.${'0'.repeat(precision - 1)}1`;
  const maxNumber = `${'9'.repeat(14)}.${'9'.repeat(precision)}`;

  const errors: IPromoErrors = {
    texts: [],
  };

  if (
    ![
      PROMO_TYPE.DEVALUATION_BONUS,
      PROMO_TYPE.DEPOSIT_BONUS,
      PROMO_TYPE.FIXED_BONUS,
    ].includes(data.type)
  ) {
    errors.type = 'Invalid offer type.';
  }

  Object.entries(data).forEach(([key, value]) => {
    if (limitTypesKeys.includes(key) && value !== undefined) {
      const fieldNamesWithDecimalValue = [
        TOTAL_DEPOSIT_FROM,
        TOTAL_DEPOSIT_TO,
        TOTAL_BONUS_AMOUNT,
      ];

      errors[key] = validateWithSingleError(
        value,
        constraints.limit(
          limitTypes.find((item) => item.value === key)?.label || '',
          fieldNamesWithDecimalValue.includes(key)
        )
      );
    }
  });

  errors.name = validateWithSingleError(data.name, constraints.name);
  errors.comment = validateWithSingleError(data.comment, constraints.comment);

  errors.type = validateWithSingleError(data.type, constraints.type);

  const user = getUser(state);
  const { rights, businessUnitRights } = user!;

  errors.businessUnits = getEditBUInfoMessage({
    promoBusinessUnits: data.businessUnits || [],
    rights,
    businessUnitRights,
    businessUnits: getDictionaryByNameSelector(state, 'businessUnit'),
  });

  errors.leadRetentionFilter = validateWithSingleError(
    data.leadRetentionFilter,
    constraints.leadRetentionFilter
  );

  errors.validFrom = validateWithSingleError(
    String(data.validFrom || ''),
    constraints.validFrom
  );
  errors.validTo = validateWithSingleError(
    String(data.validTo || ''),
    constraints.validTo
  );

  errors.transactionSubType = validateWithSingleError(
    String(data.transactionSubType || ''),
    requiredValidation('Sub Type')
  );

  if (data.validFrom > data.validTo)
    errors.validFrom = 'Valid From should be less than Valid To.';

  errors.rule = {
    bonusConditions: validateRules(
      data.rule.bonusConditions,
      data.type,
      minNumber,
      maxNumber
    ),
  };

  if (data.type === PROMO_TYPE.DEVALUATION_BONUS) {
    const { depositCurrencies, devaluationCurrencies } = get(state, [
      'app',
      'config',
      'specialOffers',
    ]);

    if (!data.rule.depositCurrency) {
      errors.rule.depositCurrency = 'Deposit currency is required';
    } else {
      errors.rule.depositCurrency = validateWithSingleError(
        data.rule.depositCurrency,
        constraints.currency(depositCurrencies)
      );
    }

    if (!data.rule.devaluationCurrency) {
      errors.rule.devaluationCurrency = 'Devaluation currency is required';
    } else {
      errors.rule.devaluationCurrency = validateWithSingleError(
        data.rule.devaluationCurrency,
        constraints.currency(devaluationCurrencies)
      );
    }
  }

  errors.texts = [];

  data.texts.forEach((text: IText, i: number) => {
    errors.texts[i] = {};
    errors.texts[i].description = validateWithSingleError(
      text.description!,
      constraints.description
    );
    errors.texts[i].terms = validateWithSingleError(
      text.terms!,
      constraints.terms
    );
    errors.texts[i].emailBody = validateWithSingleError(
      text.emailBody!,
      constraints.description
    );
    errors.texts[i].emailSubject = validateWithSingleError(
      text.emailSubject!,
      constraints.emailSubject
    );
    errors.texts[i].emailFrom = validateWithSingleError(
      text.emailFrom!,
      constraints.emailFrom
    );

    if (text.languageId === 38) {
      if (!text.description)
        errors.texts[i].description = 'MyAccount description is required.';

      if (!text.emailBody)
        errors.texts[i].emailBody = 'Email description is required.';
    }
  });

  return errors;
}

function validateRules(
  rules: IBonusConditions[],
  type: string,
  minNumber: string,
  maxNumber: string
) {
  const errors: IBonusConditionsErrors[] = [];

  rules.forEach((rule: IBonusConditions, index: number) => {
    let amountFromError = validateWithSingleError(
      String(rule.amountFrom),
      constraints.amountFrom()
    );
    const amountToError = validateWithSingleError(
      String(rule.amountTo),
      constraints.amountTo()
    );

    if (!amountFromError && !amountToError) {
      if (Number(rule.amountFrom) >= Number(rule.amountTo))
        amountFromError = 'Amount From should be less than Amount To.';
    }

    errors[index] = {
      value: validateWithSingleError(
        String(rule.value),
        constraints.value(type, minNumber, maxNumber)
      ),
      amountFrom: amountFromError,
      amountTo: amountToError,
    };
  });

  return errors;
}

function validateWithSingleError(value: string, constraint: any) {
  const errorMessage = single(value, constraint, {});

  return errorMessage && errorMessage[0];
}
